import { useState } from 'react';
import { formatMoney } from '@makila/fiscal';
import { KeyRound, Plus } from 'lucide-react';
import { mzDateTime } from '../lib/receipt';
import { post } from './api';
import { Alert, Button, Card, Field, Input } from './ui';
import { attempt, useLoad } from './useLoad';

interface Terminal { id: string; code: string; name: string; status: 'pending' | 'active' | 'revoked'; last_seq: number; last_seen_at: string | null; activation_expires_at: string | null }
interface Store { id: string; code: string; name: string; address: string; kind?: 'shop' | 'vehicle'; plate?: string | null; terminals: Terminal[] }

const STATUS = { pending: 'Por activar', active: 'Activa', revoked: 'Revogada' } as const;
interface PlanInfo { current: string; trialEndsAt: string | null; bundle: boolean; plans: { code: string; name: string; monthly: string; yearly: string; bundle_discount_pct: string; max_stores: number | null; max_terminals: number | null; features: Record<string, boolean> }[] }
// O mesmo formato dos valores no resto do POS (1.700, 17.000); sem cêntimos quando são zero.
const mt = (v: string) => formatMoney(v, Number.isInteger(Number(v)) ? 0 : 2);
const when = (iso: string | null) => (iso ? mzDateTime(iso) : '—');

/** Lojas e caixas: criar uma caixa dá o código de activação, que só se mostra uma vez. */
export function StoresPage() {
  const { data, error, reload } = useLoad<{ items: Store[] }>('/stores');
  const [code, setCode] = useState<{ terminal: string; code: string; expiresAt: string } | null>(null);
  const [msg, setMsg] = useState<string | null>(null);
  const [newStore, setNewStore] = useState({ code: '', name: '' });
  const me = useLoad<{ role: string; tenant: { training_mode: boolean } }>('/auth/me');
  const [confirm, setConfirm] = useState('');
  const plan = useLoad<PlanInfo>('/plan');

  if (error) return <Alert>{error}</Alert>;
  if (!data) return <p className="text-muted">A carregar…</p>;

  return (
    <div className="max-w-4xl space-y-4">
      {code && (
        <Alert tone="ok">
          <div className="font-semibold">Código de activação da {code.terminal}</div>
          <div className="my-2 font-mono text-3xl tracking-widest">{code.code}</div>
          No equipamento da caixa, abra este endereço e escreva o código. Vale até {when(code.expiresAt)} e só serve uma vez.
          <div className="mt-2"><Button variant="ghost" onClick={() => setCode(null)}>Já anotei</Button></div>
        </Alert>
      )}
      {msg && <Alert>{msg}</Alert>}
      {me.data?.tenant.training_mode && (
        <Card title="Emissão de documentos: modo formação">
          <p className="text-sm text-muted">As caixas emitem em séries de formação (VD-FORM…), sem valor fiscal. Quando estiver pronto para vender a sério, o dono activa a emissão fiscal — de uma vez, sem volta. As caixas passam às séries reais na actualização seguinte (até 10 minutos, ou ao reabrir).</p>
          {me.data.role === 'owner' ? (
            <form className="mt-3 flex flex-wrap items-end gap-2" onSubmit={async (e) => {
              e.preventDefault();
              const x = await attempt(() => post('/settings/go-live', { confirm }));
              setMsg(x);
              if (!x) void me.reload();
            }}>
              <Field label='Escreva ACTIVAR para confirmar'><Input aria-label="Confirmação" value={confirm} onChange={(e) => setConfirm(e.target.value)} className="w-40" /></Field>
              <Button disabled={confirm !== 'ACTIVAR'}>Activar emissão fiscal</Button>
            </form>
          ) : <p className="mt-2 text-sm">Só o dono da empresa pode activar.</p>}
        </Card>
      )}
      {plan.data && (
        <Card title="Plano">
          {plan.data.bundle && <p className="mb-2 text-sm font-medium text-green-700 dark:text-green-400">Empresa com o Makila: desconto de pacote já aplicado nos preços.</p>}
          <div className="grid gap-2 sm:grid-cols-4">
            {plan.data.plans.map((p) => (
              <div key={p.code} className={`rounded-xl border-2 p-3 ${p.code === plan.data!.current ? 'border-brand-700' : 'border-base'}`}>
                <div className="font-semibold">{p.name}{p.code === plan.data!.current && <span className="ml-1 text-xs text-brand-700">(actual)</span>}</div>
                <div className="num mt-1 text-xl font-bold">{Number(p.monthly) ? `${mt(p.monthly)} MT` : 'Grátis'}<small className="text-xs font-normal text-muted">{Number(p.monthly) ? '/mês' : ''}</small></div>
                {Number(p.yearly) > 0 && <div className="text-xs text-muted">ou {mt(p.yearly)} MT/ano</div>}
                <div className="mt-1 text-xs text-muted">{p.max_stores ? `${p.max_stores} loja` : 'Lojas sem limite'} · {p.max_terminals ? `${p.max_terminals} caixa(s)` : 'caixas sem limite'}{p.features.stock ? ' · stock' : ''}</div>
              </div>
            ))}
          </div>
          <p className="mt-2 text-xs text-muted">Para mudar de plano, fale connosco.</p>
        </Card>
      )}
      {data.items.map((s) => (
        <Card key={s.id} title={`${s.name} (${s.code})${s.kind === 'vehicle' ? ` · viatura ${s.plate ?? ''}` : ''}`} actions={<NewTerminal storeId={s.id} onCreated={(c) => { setCode(c); void reload(); }} onError={setMsg} />}>
          {!s.terminals.length && <p className="text-muted">Ainda sem caixas. Crie uma para activar o primeiro equipamento.</p>}
          <ul className="divide-y divide-[var(--border)]">
            {s.terminals.map((t) => (
              <li key={t.id} className="flex flex-wrap items-center gap-3 py-3">
                <div className="min-w-0 flex-1">
                  <div className="font-medium">{t.name} <span className="text-muted">· {s.code}{t.code}</span></div>
                  <div className="text-sm text-muted">{STATUS[t.status]}{t.status === 'active' ? ` · último contacto ${when(t.last_seen_at)} · ${t.last_seq} eventos recebidos` : ''}</div>
                </div>
                {t.status === 'pending' && (
                  <Button variant="ghost" onClick={async () => {
                    let r: { activationCode: string; expiresAt: string } | null = null;
                    const err = await attempt(async () => { r = await post(`/terminals/${t.id}/activation-code`); });
                    if (err) setMsg(err); else if (r) setCode({ terminal: t.name, code: (r as { activationCode: string }).activationCode, expiresAt: (r as { expiresAt: string }).expiresAt });
                  }}><KeyRound size={16} /> Novo código</Button>
                )}
                {t.status === 'active' && (
                  <Button variant="danger" onClick={async () => {
                    const reason = prompt(`Revogar a ${t.name}? O equipamento deixa de sincronizar e vendas por enviar ficam nele.\n\nMotivo:`);
                    if (!reason) return;
                    setMsg(await attempt(() => post(`/terminals/${t.id}/revoke`, { reason })));
                    void reload();
                  }}>Revogar</Button>
                )}
              </li>
            ))}
          </ul>
        </Card>
      ))}
      <Card title="Nova loja">
        <form className="grid gap-3 sm:grid-cols-[120px_1fr_auto] sm:items-end" onSubmit={async (e) => {
          e.preventDefault();
          const err = await attempt(() => post('/stores', newStore));
          setMsg(err);
          if (!err) { setNewStore({ code: '', name: '' }); void reload(); }
        }}>
          <Field label="Código" hint="Ex.: L2"><Input required maxLength={4} value={newStore.code} onChange={(e) => setNewStore({ ...newStore, code: e.target.value.toUpperCase() })} /></Field>
          <Field label="Nome"><Input required value={newStore.name} onChange={(e) => setNewStore({ ...newStore, name: e.target.value })} /></Field>
          <Button><Plus size={16} /> Criar loja</Button>
        </form>
      </Card>
    </div>
  );
}

function NewTerminal({ storeId, onCreated, onError }: { storeId: string; onCreated: (c: { terminal: string; code: string; expiresAt: string }) => void; onError: (m: string | null) => void }) {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ code: '', name: '' });
  if (!open) return <Button variant="ghost" onClick={() => setOpen(true)}><Plus size={16} /> Nova caixa</Button>;
  return (
    <form className="flex flex-wrap items-end gap-2" onSubmit={async (e) => {
      e.preventDefault();
      let r: { activationCode: string; expiresAt: string; terminal: { name: string } } | null = null;
      const err = await attempt(async () => { r = await post(`/stores/${storeId}/terminals`, f); });
      onError(err);
      if (r) { const x = r as { activationCode: string; expiresAt: string; terminal: { name: string } }; onCreated({ terminal: x.terminal.name, code: x.activationCode, expiresAt: x.expiresAt }); setOpen(false); setF({ code: '', name: '' }); }
    }}>
      <Input aria-label="Código da caixa" required placeholder="C1" maxLength={4} className="w-20" value={f.code} onChange={(e) => setF({ ...f, code: e.target.value.toUpperCase() })} />
      <Input aria-label="Nome da caixa" required placeholder="Caixa 1" className="w-40" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} />
      <Button>Criar</Button>
      <Button type="button" variant="ghost" onClick={() => setOpen(false)}>Cancelar</Button>
    </form>
  );
}
